import React, { useState } from 'react';
import { AlertTriangle, AlertCircle, Clock, Wrench, Shield, ShieldAlert, ShieldQuestion, FileText, X, ChevronRight, Gauge } from 'lucide-react';
import { useFormat, formatDate } from '../../i18n/format.js';
import i18n from '../../i18n/index.js';

/**
 * IssueBadge - Shows multiple issues as a stacked badge
 * Features:
 * - Displays count of issues
 * - Color-coded by priority (red = urgent, amber = warning, blue = info)
 * - Expandable popover showing all issues
 * - Clickable issues for navigation
 */

const ISSUE_ICONS = {
  ct_expired: Clock,
  ct_expiring: Clock,
  insurance_expired: Shield,
  insurance_expiring: Shield,
  maintenance_overdue: Wrench,
  maintenance_due: Wrench,
  document_expired: FileText,
  document_expiring: FileText,
  claim_open: AlertTriangle,
  immobilized: AlertCircle,
  insurance_not_verified: ShieldQuestion,
  carte_grise_lost: FileText,
  carte_grise_transfer: FileText,
  insurance_discrepancy: ShieldAlert,
  resource_critical: Gauge,
  resource_warning: Gauge,
  resource_planned: Wrench,
  fuel_low: Gauge,
  default: AlertTriangle
};

const ISSUE_COLORS = {
  critical: { bg: 'bg-red-500', text: 'text-white', hover: 'hover:bg-red-600' },
  high: { bg: 'bg-amber-500', text: 'text-white', hover: 'hover:bg-amber-600' },
  medium: { bg: 'bg-yellow-400', text: 'text-yellow-900', hover: 'hover:bg-yellow-500' },
  low: { bg: 'bg-blue-400', text: 'text-white', hover: 'hover:bg-blue-500' },
  info: { bg: 'bg-gray-400', text: 'text-white', hover: 'hover:bg-gray-500' }
};

function IssueBadge({ issues = [], onIssueClick, compact = false }) {
  const { t, fmt } = useFormat();
  const [showPopover, setShowPopover] = useState(false);

  if (issues.length === 0) return null;

  // Calculate overall priority (highest priority among all issues)
  const priorityOrder = ['critical', 'high', 'medium', 'low', 'info'];
  const overallPriority = issues.reduce((highest, issue) => {
    const issuePriority = issue.priority || 'info';
    return priorityOrder.indexOf(issuePriority) < priorityOrder.indexOf(highest)
      ? issuePriority
      : highest;
  }, 'info');

  const colors = ISSUE_COLORS[overallPriority];

  // Group issues by type
  const groupedIssues = issues.reduce((acc, issue) => {
    const type = issue.type || 'other';
    if (!acc[type]) acc[type] = [];
    acc[type].push(issue);
    return acc;
  }, {});

  if (compact) {
    return (
      <span
        className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-bold ${colors.bg} ${colors.text}`}
        title={t('common.badges.issue.count', { count: issues.length })}
      >
        {issues.length}
      </span>
    );
  }

  return (
    <div className="relative inline-block">
      <button
        onClick={(e) => { e.stopPropagation(); setShowPopover(!showPopover); }}
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${colors.bg} ${colors.text} ${colors.hover} transition-colors`}
      >
        <AlertTriangle className="w-3 h-3" />
        {issues.length === 1
          ? (issues[0].shortLabel || t('common.badges.issue.count', { count: 1 }))
          : t('common.badges.issue.count', { count: issues.length })}
      </button>

      {/* Popover */}
      {showPopover && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={(e) => { e.stopPropagation(); setShowPopover(false); }}
          />

          {/* Popover Content */}
          <div className="absolute left-0 top-full mt-2 z-50 w-72 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200">
              <span className="font-semibold text-gray-700">{t('common.badges.issue.popoverTitle')}</span>
              <button
                onClick={(e) => { e.stopPropagation(); setShowPopover(false); }}
                className="p-1 rounded hover:bg-gray-200"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
            <div className="max-h-64 overflow-y-auto">
              {Object.entries(groupedIssues).map(([type, typeIssues]) => (
                <div key={type} className="border-b border-gray-100 last:border-b-0">
                  {typeIssues.map((issue, idx) => {
                    const IconComponent = ISSUE_ICONS[issue.type] || ISSUE_ICONS.default;
                    const issueColors = ISSUE_COLORS[issue.priority || 'info'];
                    const isGrouped = issue.itemCount > 1;
                    return (
                      <button
                        key={idx}
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowPopover(false);
                          onIssueClick && onIssueClick(
                            isGrouped ? { ...issue, expandGroup: true } : issue
                          );
                        }}
                        className="w-full px-3 py-2 flex items-start gap-2 hover:bg-blue-50 text-left transition-colors cursor-pointer group"
                      >
                        <span className={`mt-0.5 p-1 rounded ${issueColors.bg}`}>
                          <IconComponent className={`w-3 h-3 ${issueColors.text}`} />
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate group-hover:text-blue-600">
                            {issue.title}
                          </p>
                          {issue.description && (
                            <p className="text-xs text-gray-500 truncate">
                              {issue.description}
                            </p>
                          )}
                          {issue.dueDate && (
                            <p className="text-xs text-gray-400 mt-0.5">
                              {t('common.badges.issue.dueDatePrefix')}: {fmt.date(issue.dueDate)}
                            </p>
                          )}
                        </div>
                        {/* Visual cue — grouped items show count badge */}
                        {isGrouped && (
                          <span className="flex-shrink-0 mt-0.5 px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-gray-200 text-gray-700">
                            {issue.itemCount}
                          </span>
                        )}
                        <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-blue-500 flex-shrink-0 mt-0.5 transition-colors" />
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Helper function to calculate issues for a vehicle
 *
 * `plannedMaintenanceByVehicle` is an optional Map<vehicle_id, { scheduled_date,
 * garage_name, count }>. When the current vehicle has at least one planned MR,
 * `resource_critical` and `resource_warning` issues are softened into a single
 * `resource_planned` info-priority entry — same workaround pattern as
 * ResourceAlertsWidget. See tech_debt_alert_resource_link.md for the proper
 * post-salon fix.
 *
 * NOTE i18n: this is plain (non-React) code, invoked from other screens on
 * every render/recompute — it resolves strings via the global `i18n.t()`
 * (see I18N_CONVENTIONS.md) rather than the `useTranslation()` hook. The
 * labels below therefore only refresh when the *caller* re-renders after a
 * language change; callers outside this LOT A1 scope were not audited for
 * that subscription.
 */
export function calculateVehicleIssues(vehicle, { claims = [], maintenance = [], insurance = [], documents = [], insuranceVerification = null, insuredVehicleIds = null, plannedMaintenanceByVehicle = null } = {}) {
  const t = i18n.t.bind(i18n);
  const issues = [];
  const today = new Date();
  const warningDays = 30;
  const plannedForVehicle = plannedMaintenanceByVehicle?.get?.(vehicle.id) || null;

  const FLUID_LABELS = {
    fuel_level: t('common.badges.issue.fluid.fuel'),
    adblue_level: t('common.badges.issue.fluid.adblue'),
    oil_level: t('common.badges.issue.fluid.oil'),
    brake_fluid: t('common.badges.issue.fluid.brakeFluid'),
    coolant_level: t('common.badges.issue.fluid.coolant'),
    washer_fluid: t('common.badges.issue.fluid.washerFluid'),
  };

  // Uninsured check — P0 for active vehicles not in any active vehicle_insurance record.
  // Skipped when caller doesn't pass the set (back-compat).
  if (insuredVehicleIds && vehicle.status === 'active' && !insuredVehicleIds.has(vehicle.id)) {
    issues.push({
      type: 'insurance_missing',
      priority: 'critical',
      shortLabel: t('common.badges.issue.insuranceMissing.shortLabel'),
      title: t('common.badges.issue.insuranceMissing.title'),
      description: t('common.badges.issue.insuranceMissing.description'),
      data: { vehicleId: vehicle.id }
    });
  }

  // Check CT expiry
  if (vehicle.ct_expiry_date) {
    const ctExpiry = new Date(vehicle.ct_expiry_date);
    const daysUntilExpiry = Math.ceil((ctExpiry - today) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiry < 0) {
      issues.push({
        type: 'ct_expired',
        priority: 'critical',
        shortLabel: t('common.badges.issue.ctExpired.shortLabel'),
        title: t('common.badges.issue.ctExpired.title'),
        description: t('common.badges.issue.ctExpired.description', { count: Math.abs(daysUntilExpiry) }),
        dueDate: vehicle.ct_expiry_date
      });
    } else if (daysUntilExpiry <= warningDays) {
      issues.push({
        type: 'ct_expiring',
        priority: 'high',
        shortLabel: t('common.badges.issue.ctExpiring.shortLabel'),
        title: t('common.badges.issue.ctExpiring.title'),
        description: t('common.badges.issue.ctExpiring.description', { count: daysUntilExpiry }),
        dueDate: vehicle.ct_expiry_date
      });
    }
  }

  // Check carte grise status
  if (vehicle.carte_grise_status === 'lost') {
    issues.push({
      type: 'carte_grise_lost',
      priority: 'critical',
      shortLabel: t('common.badges.issue.carteGriseLost.shortLabel'),
      title: t('common.badges.issue.carteGriseLost.title'),
      description: t('common.badges.issue.carteGriseLost.description'),
    });
  } else if (vehicle.carte_grise_status === 'pending_transfer') {
    issues.push({
      type: 'carte_grise_transfer',
      priority: 'high',
      shortLabel: t('common.badges.issue.carteGriseTransfer.shortLabel'),
      title: t('common.badges.issue.carteGriseTransfer.title'),
      description: vehicle.carte_grise_location_detail || t('common.badges.issue.carteGriseTransfer.descriptionFallback'),
    });
  }

  // Check open claims for this vehicle
  const vehicleClaims = claims.filter(c => c.vehicle_id === vehicle.id && !['closed', 'cloture', 'rejected', 'settled'].includes(c.status));
  if (vehicleClaims.length === 1) {
    const claim = vehicleClaims[0];
    issues.push({
      type: 'claim_open',
      priority: claim.status === 'open' ? 'high' : 'medium',
      shortLabel: t('common.badges.issue.claimOpen.shortLabel'),
      title: claim.status === 'open'
        ? t('common.badges.issue.claimOpen.titleOpen')
        : t('common.badges.issue.claimOpen.titleInProgress'),
      description: claim.description?.substring(0, 50) || t('common.badges.issue.claimOpen.descriptionFallback', { id: claim.id }),
      data: { ...claim, vehicleId: vehicle.id }
    });
  } else if (vehicleClaims.length > 1) {
    issues.push({
      type: 'claim_open',
      priority: 'high',
      shortLabel: t('common.badges.issue.claimOpenMulti.shortLabel', { count: vehicleClaims.length }),
      title: t('common.badges.issue.claimOpenMulti.title', { count: vehicleClaims.length }),
      description: t('common.badges.issue.claimOpenMulti.description'),
      itemCount: vehicleClaims.length,
      items: vehicleClaims,
      data: { vehicleId: vehicle.id }
    });
  }

  // Check overdue maintenance — include all actionable statuses (not just 'scheduled')
  const ACTIVE_MAINT_STATUSES = ['scheduled', 'submitted', 'approved', 'requested'];
  const vehicleMaintenance = maintenance.filter(m => m.vehicle_id === vehicle.id && ACTIVE_MAINT_STATUSES.includes(m.status));
  const overdueItems = [];
  const upcomingItems = [];
  vehicleMaintenance.forEach(m => {
    if (m.scheduled_date) {
      const scheduledDate = new Date(m.scheduled_date);
      const daysOverdue = Math.ceil((today - scheduledDate) / (1000 * 60 * 60 * 24));
      if (daysOverdue > 0) {
        overdueItems.push({ ...m, _daysOverdue: daysOverdue });
      } else if (daysOverdue >= -warningDays) {
        upcomingItems.push({ ...m, _daysUntil: Math.abs(daysOverdue) });
      }
    }
  });

  if (overdueItems.length === 1) {
    const m = overdueItems[0];
    issues.push({
      type: 'maintenance_overdue',
      priority: 'high',
      shortLabel: t('common.badges.issue.maintenanceOverdue.shortLabel'),
      title: t('common.badges.issue.maintenanceOverdue.title'),
      description: t('common.badges.issue.maintenanceOverdue.description', {
        type: m.type || t('common.badges.issue.maintenanceOverdue.defaultType'),
        count: m._daysOverdue
      }),
      dueDate: m.scheduled_date,
      data: { ...m, vehicleId: vehicle.id }
    });
  } else if (overdueItems.length > 1) {
    issues.push({
      type: 'maintenance_overdue',
      priority: 'high',
      shortLabel: t('common.badges.issue.maintenanceOverdueMulti.shortLabel', { count: overdueItems.length }),
      title: t('common.badges.issue.maintenanceOverdueMulti.title', { count: overdueItems.length }),
      description: t('common.badges.issue.maintenanceOverdueMulti.description'),
      itemCount: overdueItems.length,
      items: overdueItems,
      data: { vehicleId: vehicle.id }
    });
  }

  if (upcomingItems.length === 1) {
    const m = upcomingItems[0];
    issues.push({
      type: 'maintenance_due',
      priority: 'medium',
      shortLabel: t('common.badges.issue.maintenanceDue.shortLabel'),
      title: t('common.badges.issue.maintenanceDue.title'),
      description: t('common.badges.issue.maintenanceDue.description', {
        type: m.type || t('common.badges.issue.maintenanceOverdue.defaultType'),
        count: m._daysUntil
      }),
      dueDate: m.scheduled_date,
      data: { ...m, vehicleId: vehicle.id }
    });
  } else if (upcomingItems.length > 1) {
    issues.push({
      type: 'maintenance_due',
      priority: 'medium',
      shortLabel: t('common.badges.issue.maintenanceDueMulti.shortLabel', { count: upcomingItems.length }),
      title: t('common.badges.issue.maintenanceDueMulti.title', { count: upcomingItems.length }),
      description: t('common.badges.issue.maintenanceDueMulti.description'),
      itemCount: upcomingItems.length,
      items: upcomingItems,
      data: { vehicleId: vehicle.id }
    });
  }

  // Check immobilized status
  if (vehicle.status === 'immobilized' || vehicle.status === 'maintenance') {
    issues.push({
      type: 'immobilized',
      priority: vehicle.status === 'immobilized' ? 'critical' : 'medium',
      shortLabel: vehicle.status === 'immobilized'
        ? t('common.badges.issue.immobilized.shortLabel')
        : t('common.badges.issue.immobilized.shortLabelMaintenance'),
      title: vehicle.status === 'immobilized'
        ? t('common.badges.issue.immobilized.title')
        : t('common.badges.issue.immobilized.titleMaintenance'),
      description: vehicle.immobilization_reason || t('common.badges.issue.immobilized.descriptionFallback')
    });
  }

  // Check resource alerts (from bulk inspection readings)
  // Split: fuel/adblue alerts → fuel workflow, everything else → maintenance
  if (vehicle._resourceAlerts) {
    const { critical, warning, fuelCritical, fuelWarning } = vehicle._resourceAlerts;
    // When a maintenance is already planned for this vehicle, fold the
    // critical+warning resource alerts into a single soft "Planifié" entry so
    // the red badge clears and operators don't double-book interventions.
    if (plannedForVehicle && (critical > 0 || warning > 0)) {
      const total = critical + warning;
      const dateLabel = plannedForVehicle.scheduled_date
        ? formatDate(plannedForVehicle.scheduled_date, { day: '2-digit', month: '2-digit' })
        : null;
      issues.push({
        type: 'resource_planned',
        priority: 'low',
        shortLabel: t('common.badges.issue.resourcePlanned.shortLabel', { count: total }),
        title: t('common.badges.issue.resourcePlanned.title', { count: total }),
        description: dateLabel
          ? t('common.badges.issue.resourcePlanned.description', {
              date: dateLabel,
              garage: plannedForVehicle.garage_name ? ` — ${plannedForVehicle.garage_name}` : ''
            })
          : t('common.badges.issue.resourcePlanned.descriptionFallback'),
        itemCount: total,
        data: { vehicleId: vehicle.id }
      });
    } else {
      // Maintenance resource alerts (brakes, tires, oil, bodywork...)
      if (critical > 0) {
        issues.push({
          type: 'resource_critical',
          priority: 'critical',
          shortLabel: t('common.badges.issue.resourceCritical.shortLabel', { count: critical }),
          title: t('common.badges.issue.resourceCritical.title', { count: critical }),
          description: t('common.badges.issue.resourceCritical.description'),
          itemCount: critical,
          data: { vehicleId: vehicle.id }
        });
      }
      if (warning > 0) {
        issues.push({
          type: 'resource_warning',
          priority: 'medium',
          shortLabel: t('common.badges.issue.resourceWarning.shortLabel', { count: warning }),
          title: t('common.badges.issue.resourceWarning.title', { count: warning }),
          description: t('common.badges.issue.resourceWarning.description'),
          itemCount: warning,
          data: { vehicleId: vehicle.id }
        });
      }
    }
    // Fluid alerts → one issue per resource type, each routes to fuel/consumable form
    const FLUID_TO_FUEL_TYPE = {
      fuel_level: null, // will use vehicle.fuel_type
      adblue_level: 'adblue', oil_level: 'engine_oil', brake_fluid: 'brake_fluid',
      coolant_level: 'coolant', washer_fluid: 'washer_fluid',
    };
    const fuelAlertsList = vehicle._resourceAlerts.fuelAlerts || [];
    for (const fa of fuelAlertsList) {
      const pct = fa.value_percent ?? 0;
      const label = FLUID_LABELS[fa.resource_type] || t('common.badges.issue.fluid.default');
      const fuelType = FLUID_TO_FUEL_TYPE[fa.resource_type] ?? (vehicle.fuel_type || 'diesel');
      const isFuel = fa.resource_type === 'fuel_level';
      issues.push({
        type: 'fuel_low',
        priority: pct <= 15 ? 'critical' : 'medium',
        shortLabel: `${label} ${pct}%`,
        title: `${label} ${pct}%`,
        description: isFuel
          ? t('common.badges.issue.fuelLow.refillNeeded')
          : t('common.badges.issue.fuelLow.topUpNeeded', { fluid: label.toLowerCase() }),
        data: { vehicleId: vehicle.id, fuelType }
      });
    }
  }

  // Check insurance verification status
  if (insuranceVerification) {
    const verData = { ...insuranceVerification, vehicleId: vehicle.id };
    if (insuranceVerification.status === 'not_verified') {
      issues.push({
        type: 'insurance_not_verified',
        priority: 'medium',
        shortLabel: t('common.badges.issue.insuranceNotVerified.shortLabel'),
        title: t('common.badges.issue.insuranceNotVerified.title'),
        description: t('common.badges.issue.insuranceNotVerified.description'),
        data: verData
      });
    } else if (insuranceVerification.status === 'verified_discrepancy') {
      issues.push({
        type: 'insurance_discrepancy',
        priority: 'high',
        shortLabel: t('common.badges.issue.insuranceDiscrepancy.shortLabel'),
        title: t('common.badges.issue.insuranceDiscrepancy.title', { count: insuranceVerification.discrepancy_count }),
        description: t('common.badges.issue.insuranceDiscrepancy.description', { count: insuranceVerification.discrepancy_count }),
        data: verData
      });
    } else if (insuranceVerification.status === 'expired') {
      issues.push({
        type: 'insurance_not_verified',
        priority: 'medium',
        shortLabel: t('common.badges.issue.verificationExpired.shortLabel'),
        title: t('common.badges.issue.verificationExpired.title'),
        description: t('common.badges.issue.verificationExpired.description'),
        data: verData
      });
    }
  }

  return issues;
}

export default IssueBadge;
